import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Agent, AgentId, Approval, Channel, ProjectId, TenantId } from "@agentsaas/protocol";
import { useServices } from "../app/services";
import { isEvent, useChannel } from "./realtime";
import { applyCollabEvent, invalidateCollab } from "./collab";
import { applyAssetEvent, invalidateAssets } from "./assets";

/** 查询 key 统一在这里定义 */
export const qk = {
  tenants: ["tenants"] as const,
  projects: (tid: string) => ["projects", tid] as const,
  project: (pid: string) => ["project", pid] as const,
  credentials: (tid: string) => ["credentials", tid] as const,
  templates: (tid?: string) => ["templates", tid ?? ""] as const,
  agents: (pid: string) => ["agents", pid] as const,
  approvals: (pid: string) => ["approvals", pid] as const,
  tree: (aid: string, path: string) => ["fs", aid, "tree", path] as const,
};

export function useTenants() {
  const { api } = useServices();
  return useQuery({ queryKey: qk.tenants, queryFn: async () => (await api.call("tenantList")).items });
}

export function useProjects(tid: string | undefined) {
  const { api } = useServices();
  return useQuery({
    queryKey: qk.projects(tid ?? ""),
    enabled: !!tid,
    queryFn: async () => (await api.call("projectList", { params: { tid: tid! }, query: { limit: 100 } })).items,
  });
}

export function useProject(pid: string | undefined) {
  const { api } = useServices();
  return useQuery({ queryKey: qk.project(pid ?? ""), enabled: !!pid, queryFn: () => api.call("projectGet", { params: { pid: pid! } }) });
}

export function useCredentials(tid: string | undefined) {
  const { api } = useServices();
  return useQuery({ queryKey: qk.credentials(tid ?? ""), enabled: !!tid, queryFn: async () => (await api.call("credentialList", { params: { tid: tid! } })).items });
}

/** 内置 + 本租户的模板（tid 缺省时由服务端按"我所在的租户"返回） */
export function useTemplates(tid?: string, opts: { enabled?: boolean } = {}) {
  const { api } = useServices();
  return useQuery({
    queryKey: qk.templates(tid),
    enabled: opts.enabled ?? true,
    queryFn: async () => (await api.call("templateList", { query: tid ? { tenantId: tid } : {} })).items,
    staleTime: 60_000,
  });
}

export function useAgents(pid: string | undefined) {
  const { api } = useServices();
  return useQuery({ queryKey: qk.agents(pid ?? ""), enabled: !!pid, queryFn: async () => (await api.call("agentList", { params: { pid: pid! } })).items });
}

export function usePendingApprovals(pid: string | undefined) {
  const { api } = useServices();
  return useQuery({
    queryKey: qk.approvals(pid ?? ""),
    enabled: !!pid,
    queryFn: async () => (await api.call("approvalList", { params: { pid: pid! }, query: { status: "pending", limit: 100 } })).items,
  });
}

/** project:<pid> 通道 → 更新 agent 列表、待审批、P2 协作数据（任务 / PR / 邮件 / 提问）与 P4 资产缓存；gap 时全量刷新 */
export function useProjectRealtime(pid: ProjectId | string | undefined) {
  const qc = useQueryClient();
  const upsertAgent = (a: Agent) =>
    qc.setQueryData<Agent[]>(qk.agents(pid!), (xs) => (xs ? (xs.some((x) => x.id === a.id) ? xs.map((x) => (x.id === a.id ? a : x)) : [...xs, a]) : xs));
  useChannel(pid ? (`project:${pid}` as Channel) : null, {
    onEvent(e) {
      if (isEvent(e, "agent.created") || isEvent(e, "agent.updated")) upsertAgent(e.data);
      else if (isEvent(e, "agent.status"))
        qc.setQueryData<Agent[]>(qk.agents(pid!), (xs) => xs?.map((x) => (x.id === e.data.agentId ? { ...x, status: e.data.status } : x)));
      else if (isEvent(e, "approval.requested"))
        qc.setQueryData<Approval[]>(qk.approvals(pid!), (xs) => (xs && !xs.some((x) => x.id === e.data.id) ? [e.data, ...xs] : xs));
      else if (isEvent(e, "approval.resolved")) qc.setQueryData<Approval[]>(qk.approvals(pid!), (xs) => xs?.filter((x) => x.id !== e.data.id));
      else if (!applyCollabEvent(qc, pid!, e)) applyAssetEvent(qc, pid!, e);
    },
    onGap() {
      invalidateCollab(qc, pid!);
      invalidateAssets(qc, pid!);
      void qc.invalidateQueries({ queryKey: qk.agents(pid!) });
      void qc.invalidateQueries({ queryKey: qk.approvals(pid!) });
    },
  });
}

export type { AgentId, TenantId };
